import { GeneralFileNodeType } from "@/enums/DocumentCenterFileType";

export namespace DocumentCenterNodeUtils {
  export function treeCountSelected(tree: DocumentCenterNode): number {
    if (!tree || !tree.children) return;
    let c = 0;
    let stack: DocumentCenterNode[][] = [tree.children];
    while (stack.length > 0) {
      const children = stack.pop();
      children?.forEach((e) => e.selected && c++);
      if (children) {
        for (const e of children) {
          stack.push(e.children);
        }
      }
    }
    return c;
  }

  export function treeFilterEmpty(tree: DocumentCenterNode): DocumentCenterNode {
    if (!tree || !tree.children) return;
    let clone = structuredClone(tree);
    let stack: DocumentCenterNode[] = [clone];
    while (stack.length > 0) {
      let node = stack.pop();
      if (node.children) {
        node.children = node.children.filter((n) => n.type === GeneralFileNodeType.FILE || n.fileCount > 0);
        for (let c of node.children) {
          stack.push(c);
        }
      }
    }
    return clone;
  }

  export function treeAllSelected(startNode: DocumentCenterNode): boolean {
    let stack: DocumentCenterNode[][] = [startNode.children];
    while (stack.length > 0) {
      const children = stack.pop();
      if (children) {
        for (let child of children) {
          if (!child.selected) {
            return false;
          }
          stack.push(child.children);
        }
      }
    }
    return true;
  }

  export function treeIsIndeterminate(startNode: DocumentCenterNode) {
    let includesAll = true;
    let includesNothing = true;
    let stack: DocumentCenterNode[][] = [startNode.children];
    while (stack.length > 0) {
      const children = stack.pop();
      children?.forEach((e) => {
        if (e.selected) {
          includesNothing = false;
        } else {
          includesAll = false;
        }
      });
      if (!includesAll && !includesNothing) return true;
      if (children) {
        for (let child of children) {
          stack.push(child.children);
        }
      }
    }
    return false;
  }

  export function treeUpdateNodeState(tree: DocumentCenterNode, target: DocumentCenterNode, state: boolean): DocumentCenterNode {
    let clone = structuredClone(tree);
    let stack: DocumentCenterNode[] = [clone];
    while (stack.length > 0) {
      let node = stack.pop();
      if (node.externalId === target?.externalId) {
        node.selected = state;
        let childStack: DocumentCenterNode[][] = [node.children];
        while (childStack.length > 0) {
          let children: DocumentCenterNode[] = childStack.pop();
          children?.forEach((c) => {
            c.selected = state;
            if (c.children) {
              childStack.push(c.children);
            }
          });
        }
        return clone;
      } else if (!target) {
        node.selected = state;
      }
      if (node.children) {
        for (let c of node.children) {
          stack.push(c);
        }
      }
    }
    return clone;
  }

  export function extractFilesFromSelected(tree: DocumentCenterNode): ExternalId[] {
    let files: ExternalId[] = [];
    let stack: DocumentCenterNode[] = [tree];
    while (stack.length > 0) {
      const node = stack.pop();
      if (node.selected && node.type === GeneralFileNodeType.FILE) {
        files.push(node.externalId);
      }
      if (node.children) {
        for (let n of node.children) {
          stack.push(n);
        }
      }
    }
    return files;
  }

  export function treeCountSelectedFiles(tree: DocumentCenterNode): number {
    if (!tree) return 0;
    let c = 0;
    let stack: DocumentCenterNode[] = [tree];
    while (stack.length > 0) {
      let node = stack.pop();
      if (node.selected) {
        if (node.type === GeneralFileNodeType.FOLDER) {
          c += node.fileCount;
        } else {
          c++;
        }
      } else if (node.children) {
        node.children.forEach((c) => stack.push(c));
      }
    }
    return c;
  }

  export function treeFindName(matches: Set<DocumentCenterNode>, search: string, tree: DocumentCenterNode) {
    let processingStack: DocumentCenterNode[] = [tree];
    while (processingStack.length > 0) {
      const node: DocumentCenterNode = processingStack.pop();
      if (node.children) {
        for (let child of node.children) {
          if (child.name.toLowerCase().includes(search.toLowerCase())) {
            matches.add(node);
            matches.add(child);
          }
          processingStack.push(child);
        }
      }
    }
  }
}

type ExternalId = string;

export type DocumentCenterNode = {
  name: string;
  type: GeneralFileNodeType;
  externalId: string;
  children?: DocumentCenterNode[];
  selected?: boolean;
  collapsed?: boolean;
  fileCount?: number;
};
